import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Table, Badge } from "react-bootstrap";
import moment from "moment";
import api from "../../utils/ApiConfig";
import { getRaisonLabel, messagesErreurs } from "../FormAbandonments/raisonsAbandon";

/**
 * Paniers abandonnés d'un client : formulaires de commande commencés, parfois
 * avec un prix affiché, jamais validés. Mêmes données que l'écran global
 * « Abandons de formulaire » (GET /bo/form-abandonments), filtrées sur ce client.
 */

const LIMITE = 50;

// Une commande créée peu après l'abandon : le client a fini par commander.
const DELAI_CONVERSION_HEURES = 2;

const PaniersAbandonnes = ({ idClient, commandes }) => {
  const [abandons, setAbandons] = useState(null);
  const [total, setTotal] = useState(0);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let annule = false;
    api
      .get(`/bo/form-abandonments`, { params: { id_fournisseur: idClient, per_page: LIMITE } })
      .then((r) => {
        if (annule) return;
        setAbandons(r.data?.data?.data || []);
        setTotal(r.data?.data?.total || 0);
      })
      .catch(() => !annule && setErreur("Impossible de charger les paniers abandonnés."));
    return () => {
      annule = true;
    };
  }, [idClient]);

  const commandeEnsuite = (abandon) => {
    const debut = moment(abandon.created_at);
    const fin = debut.clone().add(DELAI_CONVERSION_HEURES, "hours");
    return (commandes || []).find((c) => c.DateCommande && moment(c.DateCommande).isBetween(debut, fin, null, "[]"));
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="pb-3">Historique des paniers abandonnés ({total})</Card.Title>
        {erreur ? (
          <p className="text-danger">{erreur}</p>
        ) : abandons === null ? (
          <p>Chargement des paniers abandonnés...</p>
        ) : abandons.length === 0 ? (
          <p>Aucun panier abandonné.</p>
        ) : (
          <>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Adresse pick-up</th>
                  <th>Adresse de livraison</th>
                  <th>Poids</th>
                  <th>Type</th>
                  <th>Prix TTC</th>
                  <th>Statut</th>
                  <th>Tentatives / erreurs</th>
                  <th>Commandé ensuite ?</th>
                </tr>
              </thead>
              <tbody>
                {abandons.map((a) => {
                  const erreurs = messagesErreurs(a.erreurs_validation);
                  const ensuite = commandeEnsuite(a);
                  return (
                    <tr key={a.id}>
                      <td className="text-nowrap">{moment(a.created_at).format("DD/MM/YYYY HH:mm")}</td>
                      <td className="small">{a.adresse_ramassage || "-"}</td>
                      <td className="small">{a.adresse_livraison || "-"}</td>
                      <td className="small">{a.poids || "-"}</td>
                      <td className="small">{a.type_livraison || "-"}</td>
                      <td className="small text-nowrap">{a.prix_calcule_ttc ? `${a.prix_calcule_ttc} €` : "-"}</td>
                      <td>
                        <Badge bg={a.formulaire_complet ? "success" : "secondary"} className="fw-normal">
                          {a.formulaire_complet ? "Prix vu" : "Sans prix"}
                        </Badge>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {getRaisonLabel(a.raison_abandon)}
                        </div>
                      </td>
                      <td className="small">
                        {a.tentatives_submit > 0 && <div>{a.tentatives_submit} tentative(s) d'envoi</div>}
                        {erreurs.length > 0 && (
                          <div className="text-danger" style={{ fontSize: 11 }} title={erreurs.join("\n")}>
                            {erreurs.slice(0, 2).join(" · ")}
                            {erreurs.length > 2 ? ` (+${erreurs.length - 2})` : ""}
                          </div>
                        )}
                        {!a.tentatives_submit && erreurs.length === 0 && "-"}
                      </td>
                      <td className="small text-nowrap">
                        {ensuite ? (
                          <Link to={`/livraisons/show/${ensuite.id}`} className="text-success">
                            ✓ #{ensuite.id}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {total > LIMITE && (
              <Link to="/telemetry/form-abandonments" className="small">
                {total - LIMITE} plus ancien(s) : voir l'écran des abandons de formulaire
              </Link>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default PaniersAbandonnes;
